import { motion } from 'framer-motion'
import { MapPin, Clock, Phone, ExternalLink } from 'lucide-react'
import whatsappIcon from '../assets/whatsapp.png'
import {
  TELEPHONE_E164,
  PHONE_DISPLAY_BR,
} from '../data/copa2026'
import { openWhatsAppSmart } from '../utils/openWhatsAppSmart'

const Location = () => {
  const handleDirections = () => {
    window.open('https://maps.app.goo.gl/635Sgj2hZrR1pN8U9', '_blank')
  }

  const handleCall = () => {
    window.location.href = `tel:${TELEPHONE_E164}`
  }

  const handleWhatsApp = () => {
    openWhatsAppSmart()
  }

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Endereço e Horários</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Mapa */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-light-gray rounded-xl overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14631.141703060797!2d-46.6048287!3d-23.5402188!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce595b1304c109%3A0xb9f506b87ea2dba7!2sNova%20Banca!5e0!3m2!1spt-BR!2sbr!4v1752860944582!5m2!1spt-BR!2sbr"
                width="100%" 
                height="400" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Nova Banca - Localização"
                className="w-full"
              ></iframe>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleDirections}
              className="btn-primary mt-6 w-full inline-flex items-center justify-center gap-2"
            >
              <ExternalLink size={20} />
              Abrir no Google Maps
            </motion.button>
          </motion.div>

          {/* Informações */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Endereço */}
            <div className="card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-action-red rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-gray mb-3">Endereço</h3>
                  <div className="space-y-2 text-lg">
                    <p className="font-semibold">Largo São José do Belém, 61 - Belém, São Paulo - SP, 03057-040</p>
                    <p>Belenzinho</p>
                    <p>São Paulo - SP</p>
                    <p>CEP: 03057-040</p>
                    <p className="text-action-red font-medium text-sm mt-3">
                      Em frente à Padaria Belga
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Horários */}
            <div className="card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-action-red rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-gray mb-3">Horários de Funcionamento</h3>
                  <div className="space-y-3 text-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Segunda a Sexta:</span>
                      <span className="font-bold text-action-red">09:00 - 18:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Sábado:</span>
                      <span className="font-bold text-action-red">09:00 - 16:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Domingo:</span>
                      <span className="font-bold text-action-red">Fechado</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Feriados:</span>
                      <span className="font-bold text-action-red">09:00 - 15:00</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contato */}
            <div className="card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-action-red rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-gray mb-3">Dúvidas?</h3>
                  <p className="text-lg mb-2">Entre em contato conosco</p>
                  <p className="text-action-red font-semibold mb-4">{PHONE_DISPLAY_BR}</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={handleCall}
                      className="btn-secondary inline-flex items-center justify-center gap-2"
                    >
                      <Phone size={20} />
                      Ligar
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={handleWhatsApp}
                      className="inline-flex items-center justify-center gap-2 bg-green-600 text-white font-bold px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <img
                        src={whatsappIcon}
                        alt=""
                        aria-hidden="true"
                        className="w-5 h-5 object-contain"
                      />
                      WhatsApp
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Location 